import Page from 'attendee/theme/Page'
import React from 'react'
import styled from 'styled-components'
import {WhyOnlineEvent} from 'attendee/theme/WhyOnlineEvent'
import {Helmet} from 'react-helmet'
import {DEFAULT_OG_IMAGE, SITE_ORIGIN} from 'seo/siteMeta'

// Icons
import LinkIcon from '@mui/icons-material/Link'
import PaidIcon from '@mui/icons-material/Paid'
import InsightsIcon from '@mui/icons-material/Insights'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import AvTimerIcon from '@mui/icons-material/AvTimer'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import GroupsIcon from '@mui/icons-material/Groups'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'

import {routes} from 'routes/routes'
import {useI18n} from 'i18n/I18nContext'
import {withLocalePath} from 'i18n/localePaths'

export function ForAffiliates() {
  const {locale, t} = useI18n()
  const pageUrl = `${SITE_ORIGIN}${withLocalePath(locale, routes.affiliates)}`
  const orgLoginUrl = withLocalePath(locale, routes.organization.login)

  return (
    <Page>
      <>
        <Helmet>
          <title>{t('affMar.metaTitle')}</title>
          <meta name="description" content={t('affMar.metaDescription')} />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:title" content={t('affMar.ogTitle')} />
          <meta property="og:description" content={t('affMar.ogDescription')} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:secure_url" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={t('affMar.ogTitle')} />
          <meta
            name="twitter:description"
            content={t('affMar.ogDescription')}
          />
          <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        </Helmet>

        <section className="contact_section dark_light_class pt-55 pb-55">
          <div className="container">
            <div className="section__title-wrapper mb-80">
              <span className="section__back-title">
                {t('affMar.heroBack')}
              </span>
              <span className="section__subtitle">
                {t('affMar.pageSubtitle')}
              </span>
              <h1 className="section__title">
                {t('affMar.pageTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('affMar.pageTitleHighlight')}
                </span>
              </h1>
            </div>

            <Hero className="mb-80">
              <HeroMain>
                <HeroBadge>{t('affMar.heroBadge')}</HeroBadge>
                <HeroTitle>
                  {t('affMar.heroCommissionLineBefore')}{' '}
                  <span className="text__highlight">
                    {t('affMar.heroCommission')}
                  </span>{' '}
                  {t('affMar.heroCommissionLineAfter')}
                </HeroTitle>
                <HeroSubtitle>{t('affMar.heroSubtitle')}</HeroSubtitle>

                <HeroCtas>
                  <PrimaryCta href={orgLoginUrl}>
                    {t('affMar.ctaPartner')}
                  </PrimaryCta>
                </HeroCtas>

                <HeroFine>{t('affMar.heroFine')}</HeroFine>
              </HeroMain>

              <HeroSide>
                <MiniCard>
                  <MiniIcon>
                    <PaidIcon />
                  </MiniIcon>
                  <div>
                    <MiniTitle>{t('affMar.mini1Title')}</MiniTitle>
                    <MiniText>{t('affMar.mini1Text')}</MiniText>
                  </div>
                </MiniCard>

                <MiniCard>
                  <MiniIcon>
                    <AvTimerIcon />
                  </MiniIcon>
                  <div>
                    <MiniTitle>{t('affMar.mini2Title')}</MiniTitle>
                    <MiniText>{t('affMar.mini2Text')}</MiniText>
                  </div>
                </MiniCard>

                <MiniCard>
                  <MiniIcon>
                    <InsightsIcon />
                  </MiniIcon>
                  <div>
                    <MiniTitle>{t('affMar.mini3Title')}</MiniTitle>
                    <MiniText>{t('affMar.mini3Text')}</MiniText>
                  </div>
                </MiniCard>
              </HeroSide>
            </Hero>

            <SectionHeader className="mt-90 mb-30">
              <span className="section__subtitle">
                {t('affMar.mathSubtitle')}
              </span>
              <h2 className="section__title">
                {t('affMar.mathTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('affMar.mathTitleHighlight')}
                </span>{' '}
                {t('affMar.mathTitleAfter')}
              </h2>
              <p style={{opacity: 0.9, marginTop: 10}}>
                {t('affMar.mathLead')}
              </p>
            </SectionHeader>

            <ProfitExampleContainer className="mb-80">
              <ProfitGrid>
                <ProfitCard>
                  <ProfitIcon>
                    <GroupsIcon />
                  </ProfitIcon>
                  <ProfitTitle>{t('affMar.s1Title')}</ProfitTitle>
                  <ProfitText>{t('affMar.s1Text')}</ProfitText>
                  <ProfitCalc>
                    <div>{t('affMar.s1Line1')}</div>
                    <div>{t('affMar.s1Line2')}</div>
                    <div className="total">
                      {t('affMar.s1Total')} <b>{t('affMar.s1TotalValue')}</b>
                    </div>
                  </ProfitCalc>
                </ProfitCard>

                <ProfitCard>
                  <ProfitIcon>
                    <TrendingUpIcon />
                  </ProfitIcon>
                  <ProfitTitle>{t('affMar.s2Title')}</ProfitTitle>
                  <ProfitText>{t('affMar.s2Text')}</ProfitText>
                  <ProfitCalc>
                    <div>{t('affMar.s2Line1')}</div>
                    <div>{t('affMar.s2Line2')}</div>
                    <div className="total">
                      {t('affMar.s2Total')} <b>{t('affMar.s2TotalValue')}</b>
                    </div>
                  </ProfitCalc>
                </ProfitCard>

                <ProfitCard className="highlighted">
                  <ProfitRibbon>{t('affMar.topPotential')}</ProfitRibbon>
                  <ProfitIcon>
                    <AccountBalanceIcon />
                  </ProfitIcon>
                  <ProfitTitle>{t('affMar.s3Title')}</ProfitTitle>
                  <ProfitText>{t('affMar.s3Text')}</ProfitText>
                  <ProfitCalc>
                    <div>{t('affMar.s3Line1')}</div>
                    <div>{t('affMar.s3Line2')}</div>
                    <div className="total">
                      {t('affMar.s3Total')} <b>{t('affMar.s3TotalValue')}</b>
                    </div>
                  </ProfitCalc>
                </ProfitCard>
              </ProfitGrid>
            </ProfitExampleContainer>

            {/* WHY RECOMMEND US (Value Prop) */}
            <SectionHeader className="mt-80 mb-30">
              <span className="section__subtitle">
                {t('affMar.whySubtitle')}
              </span>
              <h2 className="section__title">
                {t('affMar.whyTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('affMar.whyTitleHighlight')}
                </span>
              </h2>
            </SectionHeader>

            <TwoCol className="mb-80">
              <TextCard>
                <CardTitle>{t('affMar.solveTitle')}</CardTitle>
                <CardText>{t('affMar.solveIntro')}</CardText>
                <Bullets>
                  <li>{t('affMar.solveB1')}</li>
                  <li>{t('affMar.solveB2')}</li>
                  <li>
                    {t('affMar.solveB3Before')}
                    <b>{t('affMar.solveB3Bold')}</b>
                    {t('affMar.solveB3After')}
                  </li>
                  <li>{t('affMar.solveB4')}</li>
                  <li>{t('affMar.solveB5')}</li>
                </Bullets>
              </TextCard>

              <TextCard>
                <CardTitle>{t('affMar.promoTitle')}</CardTitle>
                <CardText>{t('affMar.promoIntro')}</CardText>
                <Bullets>
                  <li>
                    <i>{t('affMar.promoQ1')}</i>
                  </li>
                  <li>
                    <i>{t('affMar.promoQ2')}</i>
                  </li>
                  <li>
                    <i>{t('affMar.promoQ3')}</i>
                  </li>
                  <li>
                    <i>{t('affMar.promoQ4')}</i>
                  </li>
                </Bullets>
              </TextCard>
            </TwoCol>

            {/* TARGET AUDIENCE - WHO TO PROMOTE TO */}
            <SectionHeader className="mt-90 mb-30">
              <span className="section__subtitle">
                {t('affMar.audienceSubtitle')}
              </span>
              <h2 className="section__title">
                {t('affMar.audienceTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('affMar.audienceTitleHighlight')}
                </span>
                {t('affMar.audienceTitleAfter')}
              </h2>
            </SectionHeader>

            <TargetGrid className="mb-80">
              <TargetCard>
                <CheckCircleOutlineIcon />
                <div>
                  <h4>{t('affMar.target1Title')}</h4>
                  <p>{t('affMar.target1Text')}</p>
                </div>
              </TargetCard>
              <TargetCard>
                <CheckCircleOutlineIcon />
                <div>
                  <h4>{t('affMar.target2Title')}</h4>
                  <p>{t('affMar.target2Text')}</p>
                </div>
              </TargetCard>
              <TargetCard>
                <CheckCircleOutlineIcon />
                <div>
                  <h4>{t('affMar.target3Title')}</h4>
                  <p>{t('affMar.target3Text')}</p>
                </div>
              </TargetCard>
              <TargetCard>
                <CheckCircleOutlineIcon />
                <div>
                  <h4>{t('affMar.target4Title')}</h4>
                  <p>{t('affMar.target4Text')}</p>
                </div>
              </TargetCard>
            </TargetGrid>

            {/* HOW IT WORKS */}
            <SectionHeader className="mt-90 mb-30">
              <span className="section__subtitle">
                {t('affMar.processSubtitle')}
              </span>
              <h2 className="section__title">
                {t('affMar.processTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('affMar.processTitleHighlight')}
                </span>
              </h2>
            </SectionHeader>

            <Steps className="mb-80">
              <Step>
                <StepIndex>1</StepIndex>
                <StepTitle>{t('affMar.flow1Title')}</StepTitle>
                <StepText>{t('affMar.flow1Text')}</StepText>
              </Step>

              <Step>
                <StepIndex>2</StepIndex>
                <StepTitle>{t('affMar.flow2Title')}</StepTitle>
                <StepText>{t('affMar.flow2Text')}</StepText>
              </Step>

              <Step>
                <StepIndex>3</StepIndex>
                <StepTitle>{t('affMar.flow3Title')}</StepTitle>
                <StepText>{t('affMar.flow3Text')}</StepText>
              </Step>

              <Step>
                <StepIndex>4</StepIndex>
                <StepTitle>{t('affMar.flow4Title')}</StepTitle>
                <StepText>{t('affMar.flow4Text')}</StepText>
              </Step>
            </Steps>

            {/* FAQ SECTION */}
            <SectionHeader className="mt-90 mb-30">
              <span className="section__subtitle">
                {t('affMar.faqSubtitle')}
              </span>
              <h2 className="section__title">
                {t('affMar.faqTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('affMar.faqTitleHighlight')}
                </span>
              </h2>
            </SectionHeader>

            <TwoCol className="mb-80">
              <FeatureCard>
                <IconTop>
                  <AccountBalanceIcon />
                </IconTop>
                <CardTitle>{t('affMar.faq1Title')}</CardTitle>
                <CardText>{t('affMar.faq1Text')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <HelpOutlineIcon />
                </IconTop>
                <CardTitle>{t('affMar.faq2Title')}</CardTitle>
                <CardText>{t('affMar.faq2Text')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <LinkIcon />
                </IconTop>
                <CardTitle>{t('affMar.faq3Title')}</CardTitle>
                <CardText>{t('affMar.faq3Text')}</CardText>
              </FeatureCard>

              <FeatureCard>
                <IconTop>
                  <SupportAgentIcon />
                </IconTop>
                <CardTitle>{t('affMar.faq4Title')}</CardTitle>
                <CardText>{t('affMar.faq4Text')}</CardText>
              </FeatureCard>
            </TwoCol>

            {/* CTA */}
            <CtaBox className="mt-90">
              <CtaTitle>{t('affMar.endCtaTitle')}</CtaTitle>
              <CtaText>{t('affMar.endCtaText')}</CtaText>
              <CtaRow>
                <PrimaryCta href={orgLoginUrl}>
                  {t('affMar.endCtaPrimary')}
                </PrimaryCta>
                <SecondaryCta href={orgLoginUrl}>
                  {t('affMar.endCtaSecondary')}
                </SecondaryCta>
              </CtaRow>
            </CtaBox>
          </div>
        </section>

        <WhyOnlineEvent />
      </>
    </Page>
  )
}

/* ----------------------- styles ----------------------- */
const SectionHeader = styled.div``

const Hero = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 992px) {
    grid-template-columns: 1.2fr 0.8fr;
    gap: 18px;
    align-items: start;
  }
`

const HeroMain = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 22px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.05);
`

const HeroSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const HeroBadge = styled.div`
  display: inline-flex;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  font-weight: 700;
  margin-bottom: 10px;
`

const HeroTitle = styled.h2`
  margin: 0 0 10px 0;
  font-weight: 950;
  line-height: 1.15;
`

const HeroSubtitle = styled.p`
  margin: 0 0 12px 0;
  opacity: 0.9;
  line-height: 1.55;
`

const HeroFine = styled.div`
  margin-top: 14px;
  font-size: 0.95rem;
  opacity: 0.85;
`

const HeroCtas = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 14px;
`

const PrimaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 8px;
  text-decoration: none;
  color: #fff;
  font-weight: 950;
  background: var(--clr-gradient-14);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.12);
    color: #fff;
  }
`

const SecondaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--clr-common-black);
  font-weight: 950;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.12);

  &:hover {
    border-color: rgba(0, 0, 0, 0.22);
    color: var(--clr-common-black);
  }
`

const MiniCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 12px;
  align-items: start;
`

const MiniIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;

  & > svg {
    font-size: 2rem;
  }
`

const MiniTitle = styled.div`
  font-weight: 950;
  margin-top: 2px;
`

const MiniText = styled.div`
  margin-top: 6px;
  opacity: 0.9;
  line-height: 1.5;
`

const ProfitExampleContainer = styled.div`
  background: #f9f9f9;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`

const ProfitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const ProfitRibbon = styled.div`
  position: absolute;
  top: -12px;
  right: 20px;
  background: var(--clr-theme-1);
  color: white;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 4px 10px;
  border-radius: 20px;
`

const ProfitCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  position: relative;

  &.highlighted {
    border-color: var(--clr-theme-1);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  }
`

const ProfitIcon = styled.div`
  margin-bottom: 12px;
  & > svg {
    font-size: 2.5rem;
    color: var(--clr-theme-1);
  }
`

const ProfitTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 950;
  margin-bottom: 8px;
`

const ProfitText = styled.p`
  font-size: 0.95rem;
  opacity: 0.9;
  margin-bottom: 16px;
  flex-grow: 1;
`

const ProfitCalc = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 12px;
  font-size: 0.9rem;

  & > div {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;

    span {
      opacity: 0.8;
    }
  }

  & > .total {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    font-size: 1.1rem;
    color: var(--clr-theme-1);
  }
`

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
`

const TextCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
`

const FeatureCard = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`

const IconTop = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;

  & > svg {
    font-size: 2rem;
    color: var(--clr-common-black);
  }
`

const CardTitle = styled.h4`
  margin: 0 0 10px 0;
  font-weight: 950;
  font-size: 1.1rem;
  line-height: 1.3;
`

const CardText = styled.p`
  margin: 0;
  opacity: 0.92;
  line-height: 1.55;
`

const Bullets = styled.ul`
  margin: 14px 0 0 0;
  padding-left: 18px;

  & > li {
    margin: 8px 0;
    line-height: 1.4;
  }
`

const TargetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`

const TargetCard = styled.div`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  & > svg {
    color: var(--clr-theme-1);
    font-size: 1.8rem;
    flex-shrink: 0;
    margin-top: 2px;
  }

  h4 {
    margin: 0 0 4px 0;
    font-size: 1.05rem;
    font-weight: bold;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.85;
    line-height: 1.4;
  }
`

const Steps = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const Step = styled.div`
  background: #f5f5f5;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`

const StepIndex = styled.div`
  width: 38px;
  height: 38px;
  line-height: 38px;
  text-align: center;
  border-radius: 50%;
  background: var(--clr-gradient-14);
  color: #fff;
  font-weight: 950;
  font-size: 1.1rem;
  margin-bottom: 12px;
`

const StepTitle = styled.div`
  font-weight: 950;
  font-size: 1.1rem;
  margin-bottom: 8px;
`

const StepText = styled.div`
  opacity: 0.92;
  line-height: 1.5;
  font-size: 0.95rem;
`

const CtaBox = styled.div`
  border-radius: 12px;
  padding: 30px;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.02));
  border: 1px solid rgba(0, 0, 0, 0.08);
`

const CtaTitle = styled.h3`
  margin: 0 0 10px 0;
  font-weight: 950;
`

const CtaText = styled.p`
  margin: 0 0 20px 0;
  opacity: 0.9;
  line-height: 1.55;
  max-width: 800px;
`

const CtaRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`
